import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import layoutTypes from './layoutTypes';
import type { LayoutType } from './layoutTypes';
import { preloadReducePages } from './reduceRouteImports';

import getApplicationSelector from '/business/shared/state/selectors/application/getApplicationSelector';
import isReducePageUnlockedSelector from '/business/shared/state/selectors/application/isReducePageUnlockedSelector';

import useScroll from '/shared/components/functional/useScroll';

import MiscLayoutHeader from './layouts/misc-layout/MiscLayoutHeader';
import StyledFlexSpacer from '/shared/styled/StyledFlexSpacer';
import MiscLayoutFooter from './layouts/misc-layout/MiscLayoutFooter';
import StyledContainer from '/shared/styled/StyledContainer';
import StyledInner from '/shared/styled/StyledInner';
import AuthLayoutHeader from './layouts/auth-layout/AuthLayoutHeader';
import AuthLayoutFooter from './layouts/auth-layout/AuthLayoutFooter';

import Loading from '/shared/components/Loading';
import ApplicationLayoutHeader from './layouts/application-layout/ApplicationLayoutHeader';
import ApplicationLayoutFooter from './layouts/application-layout/ApplicationLayoutFooter';

import StyledReducePageV3 from '/business/shared/components/pages/reduce/v3/__styled__/StyledReducePageV3';
import ReduceHeaderNav from '/business/shared/components/pages/reduce/v3/nav/ReduceHeaderNav';
import ReduceFooterNav from '/business/shared/components/pages/reduce/v3/nav/ReduceFooterNav';
import ReductionMigrationModalTrigger from '/business/shared/components/pages/reduce/v3/ReductionMigrationModalTrigger';
import type { RootState } from '/business/shared/state/store';

type LayoutWrapperProps = {
  children: ReactNode;
  layout: LayoutType;
  hasFooter: boolean;
  hasFooterFixedToBottom: boolean;
};

function LayoutWrapper({ children, layout, hasFooter, hasFooterFixedToBottom }: LayoutWrapperProps) {
  const currentApplication = useSelector(getApplicationSelector);
  const stateIsReductionOnboarded = useSelector(isReducePageUnlockedSelector);
  const stateApplicationIsFetching = useSelector((state: RootState) => state.applications?.fetching) || false;
  const stateApplicationIsSyncingFormState =
    useSelector((state: RootState) => state.applications?.syncingFormState) || false;
  const stateApplicationIsLoading = stateApplicationIsFetching || stateApplicationIsSyncingFormState;
  const scrollRef = useRef(null);
  useScroll(scrollRef);

  useEffect(() => {
    if (layout === layoutTypes.REDUCEV3) {
      preloadReducePages().catch((error: unknown) => {
        console.error(error);
      });
    }
  }, [layout]);

  if (layout === layoutTypes.REDUCEV3) {
    return (
      <>
        <ApplicationLayoutHeader />
        <StyledContainer ref={scrollRef} $mainContent id="main-content">
          <StyledInner $mainContent $business>
            <StyledContainer $centred>
              <StyledInner $business>
                <StyledReducePageV3>
                  {stateApplicationIsLoading && !currentApplication ? (
                    <Loading />
                  ) : (
                    <>
                      <ReductionMigrationModalTrigger />
                      {stateIsReductionOnboarded && <ReduceHeaderNav />}
                      {children}
                      {stateIsReductionOnboarded && <ReduceFooterNav />}
                    </>
                  )}
                </StyledReducePageV3>
              </StyledInner>
            </StyledContainer>
          </StyledInner>
        </StyledContainer>
        <ApplicationLayoutFooter />
      </>
    );
  }

  if (layout === layoutTypes.AUTH_REPORT) {
    return (
      <>
        <AuthLayoutHeader />
        <StyledContainer $centred>
          <StyledInner $business>{children}</StyledInner>
        </StyledContainer>
        <StyledFlexSpacer />
        <AuthLayoutFooter />
      </>
    );
  }

  if (layout === layoutTypes.APP) {
    return (
      <>
        <ApplicationLayoutHeader />
        <StyledContainer ref={scrollRef} $mainContent id="main-content">
          <StyledInner $mainContent $business>
            {stateApplicationIsLoading && !currentApplication ? <Loading /> : children}
          </StyledInner>
        </StyledContainer>
        <ApplicationLayoutFooter />
      </>
    );
  }

  if (layout === layoutTypes.EMPTY) {
    return <>{children}</>;
  }

  if (layout === layoutTypes.AUTH) {
    return (
      <>
        <AuthLayoutHeader />
        <StyledContainer ref={scrollRef} $mainContent id="main-content">
          <StyledInner $mainContent $business>
            {children}
          </StyledInner>
        </StyledContainer>
        <StyledFlexSpacer />
        <AuthLayoutFooter />
      </>
    );
  }

  if (layout === layoutTypes.MISC) {
    return (
      <>
        <MiscLayoutHeader />
        <StyledContainer $centred>
          <StyledInner $business>{children}</StyledInner>
        </StyledContainer>
        <StyledFlexSpacer />
        {hasFooter && <MiscLayoutFooter />}
        {hasFooterFixedToBottom && <MiscLayoutFooter $isFixedToBottom />}
      </>
    );
  }

  return null;
}

export default LayoutWrapper;
